/**
 * PrmomtionListingResults to show suggestion of what to show after something is bought or added to cart.
 *
 * @module views/components/EventListingResults
 * @memberof -Common
 */
import './EventListingResults.scss';

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import classNames from 'classnames';
import PropTypes from 'prop-types';

import { useUserContext } from '@ulta/core/providers/UserContextProvider/UserContextProvider';
import constants from '@ulta/core/utils/constants/constants';
import { spreadProps } from '@ulta/core/utils/dom/dom';
import { getGeoLocation } from '@ulta/core/utils/getGeoLocation/getGeoLocation';

import EventCard from '@ulta/components/EventCard/EventCard';
import ProductListingWrapper from '@ulta/components/ProductListingWrapper/ProductListingWrapper';

import { composeApplyStoreFilter, composePostLoginRSVP, getUserAppliedFilterCount } from '@ulta/utils/event/event';

/**
 * Represents a EventListingResults component
 *
 * @method
 * @param {EventListingResultsProps} props - React properties passed from composition
 * @returns EventListingResults
 */

export const EventListingResults = function( props ){
  const { loading, invokeMutation, id, invokeAction, applyStoreFilterAction } = props;

  const { user, store } = useUserContext();
  const hasCheckedPostLoginStatus = useRef( false );
  const [searchParams] = useSearchParams();
  const userAppliedFiltersLength = getUserAppliedFilterCount( { urlParams: searchParams } );
  const [isGeolocationEnabled, setIsGeolocationEnabled] = useState( false );

  // Check to see if user has geolocation enabled and update location
  useEffect( () => {
    getGeoLocation().then( ( res ) => {
      if( res === undefined ){ // Geolocation is not enabled
        setIsGeolocationEnabled( false );
      }
      else { // Geolocation is enabled
        setIsGeolocationEnabled( true );
      }
    } );
  }, [] );

  /**
 * Listens for store value and user enabling geolocation
 */
  useEffect( composeApplyStoreFilter(
    { store, id, applyStoreFilterAction, isGeolocationEnabled, userAppliedFiltersLength },
    { invokeAction }
  ), [store, isGeolocationEnabled, userAppliedFiltersLength] );

  /**
 * Listens for post login response
 */
  useEffect( composePostLoginRSVP(
    { loading, loginStatus: user?.loginStatus, hasCheckedPostLoginStatus },
    { invokeMutation }
  ), [loading, hasCheckedPostLoginStatus, user?.loginStatus] );

  return (
    <ProductListingWrapper
      { ...spreadProps( { ...props } ) }
      productsViewedLabel={ props.promotionsViewedLabel }
      productsViewedTemplateLabel={ props.promotionsViewedLabelTemplate }
      itemSelectorTemplate='[data-event-id="{id}"] .pal-c-Link'
      itemSelectorClassName='EventListingResults__eventCard'
      itemSelectorAttribute='data-event-id'
      itemIndexAttribute='eventId'
      className='EventListingResults'
      loadPreviousAction={ null }
      overlayAriaLabel={ 'Events Filters' }
    >
      { ( { renderChildren, onIntersectionEvent } ) => (
        <ul className='EventListingResults__eventList'
          data-test='event-list'
        >
          { renderChildren( ( event, index ) => (
            <li className={ classNames( 'EventListingResults__eventCard', {
              'EventListingResults__eventCardGWP': !props.isGrid
            } ) }
            data-test='products-list-item'
            key={ event.eventId }
            data-event-id={ event.eventId }
            aria-label='event'
            role='group'
            >
              <EventCard
                image={ event.image }
                eyebrow={ event.eyebrow }
                headline={ event.headline }
                description={ event.description }
                schedule={ event.schedule }
                onIntersectionEvent={ onIntersectionEvent }
                location={ event.location }
                signInAction={ props.signInAction }
                rsvpAction={ event.rsvpAction }
                cancelRsvpAction={ event.cancelRsvpAction }
                invokeMutation={ props.invokeMutation }
                eventId={ event.eventId }
                eventData={ event.eventData }
                loading={ props.loading }
                cardAction={ event.cardAction }
              />
            </li>
          ) ) }
        </ul>
      ) }
    </ProductListingWrapper>
  );
};



/**
 * Property type definitions
 *
 * @typedef EventListingResultsProps
 * @type {object}
 * @property {array} items - Sets the items of the page
 * @property {boolean} loading - Flag to render the data or not
 * @property {object} filtersColumn - Sets the filters column for the page
 * @property {string} resultCountLabel - Sets the result count label for the page
 * @property {string} noResultsLabel - Sets the no result label for the page
 * @property {object} loadMoreAction - Sets the loadMoreAction for the page
 * @property {object} loadMoreUrl - Sets the load more client side url
 * @property {string} promotionsViewedLabel - Sets the promotionsViewedLabel for the page
 * @property {string} promotionsViewedLabelTemplate - Sets the promotionsViewedLabelTemplate for the page
 * @property {string} noFilteredResultsLabel - Sets the noFilteredResultsLabel for the page
 * @property {object} resetFiltersAction - Sets the resultFIltersAction for the page
 * @property {string} resetFiltersUrl - Sets the url for after a filter reset
 * @property {string} currentUrl - Sets the current url for page
 * @property {boolean} isGrid - Flag to render BMSM or GWP
 */
export const propTypes = {
  items: PropTypes.array,
  loading: PropTypes.bool,
  filtersColumn: PropTypes.object,
  resultCountLabel: PropTypes.string,
  noResultsLabel: PropTypes.string,
  loadMoreAction: PropTypes.object,
  loadMoreUrl: PropTypes.string,
  promotionsViewedLabel: PropTypes.string,
  promotionsViewedLabelTemplate: PropTypes.string,
  noFilteredResultsLabel: PropTypes.string,
  resetFiltersAction: PropTypes.object,
  resetFiltersUrl: PropTypes.string,
  currentUrl: PropTypes.string,
  isGrid: PropTypes.bool
};

export const defaultProps = {
  ...constants.INTERSECTION_OBSERVER_OPTIONS
};
EventListingResults.propTypes = propTypes;
EventListingResults.defaultProps = defaultProps;

export default EventListingResults;
